/**
 * Admin — learn.4klabs.ai learner accounts.
 *
 * Who signed up, how much they have watched, and the one lever the MVP needs:
 * disable an account. Anything deeper is a click away in the actor lens, which
 * reads the same analytics rows rather than a second tally.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Search, BarChart3, Ban, Check, Library} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LearnerAccessDialog } from '../../components/learn/LearnerAccessDialog';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import {
  adminListLearners,
  adminSetLearnerStatus,
  type AdminLearner,
  type AdminSchool,
} from '../../services/learnAPI';
import { SchoolsPanel } from '../../components/learn/SchoolsPanel';

export const Route = createFileRoute('/admin/learners')({ component: AdminLearners });

function hours(ms: number): string {
  if (!ms) return '—';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

function when(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function AdminLearners() {
  const [learners, setLearners] = useState<AdminLearner[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Which learner's library is open for editing.
  const [assigning, setAssigning] = useState<{ id: string; label: string } | null>(null);
  const [school, setSchool] = useState<AdminSchool | null>(null);

  const load = useCallback(async (q?: string) => {
    setLoading(true);
    setError(null);
    try {
      setLearners(await adminListLearners(q, school?.id));
    } catch (e) {
      setError(
        (e as { response?: { status?: number } })?.response?.status === 403
          ? 'This account is not an admin.'
          : 'Could not load learners.',
      );
    } finally {
      setLoading(false);
    }
  }, [school]);

  useEffect(() => {
    void load();
  }, [load]);

  const setStatus = async (learner: AdminLearner, next: AdminLearner['status']) => {
    setBusyId(learner.id);
    try {
      await adminSetLearnerStatus(learner.id, next);
      setLearners((rows) => rows.map((r) => (r.id === learner.id ? { ...r, status: next } : r)));
    } catch {
      setError('Could not update that account.');
    } finally {
      setBusyId(null);
    }
  };

  const active = learners.filter((l) => l.status === 'ACTIVE').length;
  const pending = learners.filter((l) => l.status === 'PENDING').length;

  return (
    <div className="container mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Learners</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Accounts on learn.4klabs.ai. {learners.length} total
            {learners.length > 0 && `, ${active} active`}
            {pending > 0 && `, ${pending} awaiting approval`}.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load(query)} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <SchoolsPanel
        selectedSchoolId={school?.id ?? null}
        onSelectSchool={(next) => {
          setQuery('');
          setSchool(next);
        }}
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void load(query);
        }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email"
            className="pl-9"
          />
        </div>
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {school ? `Accounts — ${school.name}` : 'Accounts'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
          ) : learners.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              No learners yet. Accounts appear here the first time someone signs in.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Learner</TableHead>
                  <TableHead className="text-right">Lessons</TableHead>
                  <TableHead className="text-right">Completed</TableHead>
                  <TableHead className="text-right">Watch time</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Library</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {learners.map((l) => (
                  <TableRow key={l.id} className={l.status === 'DISABLED' ? 'opacity-60' : undefined}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{l.displayName || '—'}</span>
                        {l.status === 'DISABLED' && <Badge variant="destructive">Disabled</Badge>}
                        {l.status === 'PENDING' && <Badge variant="secondary">Awaiting approval</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {l.email}
                        {l.schoolName && ` · ${l.schoolName}`}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{l.videos || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.completes || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{hours(l.watchMs)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{when(l.lastLoginAt)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{when(l.createdAt)}</TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 px-2 text-xs"
                        title="Choose which classes and lessons this account can see"
                        onClick={() => setAssigning({ id: l.id, label: l.displayName || l.email })}
                      >
                        <Library className="h-3.5 w-3.5" />
                        {l.accessMode === 'ALL'
                          ? 'All lessons'
                          : l.grantCount
                            ? `${l.grantCount} assigned`
                            : 'Assign…'}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button asChild variant="ghost" size="sm" title="Open in the actor lens">
                          <Link to="/analytics/actors/$actorId" params={{ actorId: l.actorId }}>
                            <BarChart3 className="h-4 w-4" />
                          </Link>
                        </Button>
                        {l.status === 'PENDING' ? (
                          <Button
                            size="sm"
                            disabled={busyId === l.id}
                            onClick={() => void setStatus(l, 'ACTIVE')}
                          >
                            <Check className="mr-1.5 h-4 w-4" /> Approve
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyId === l.id}
                            onClick={() => void setStatus(l, l.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE')}
                            title={l.status === 'ACTIVE' ? 'Disable this account' : 'Re-enable'}
                          >
                            {l.status === 'ACTIVE' ? <Ban className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {assigning && (
        <LearnerAccessDialog
          learnerId={assigning.id}
          learnerLabel={assigning.label}
          onClose={() => setAssigning(null)}
        />
      )}
    </div>
  );
}
